import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import type { OutreachPitchKind } from 'fym-shared/src/music24/outreach/dailyPitches';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { SamplePitch } from '../outreachTypes';
import { campaignMessages as messages } from './campaignMessages';
import { SampleKindTabs } from './SampleKindTabs';
import { SamplePitchNotice } from './SamplePitchNotice';
import { samplePitchProblem, type SamplePitchProblem } from './samplePitchProblem';
import { SamplePitchText } from './SamplePitchText';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MessagePreviewProps {
  accountId: number;
  campaignUuid: string;
  /** The kinds of the campaign: one tab each. */
  kinds: OutreachPitchKind[];
}

/** The key of the samples of a campaign with no kinds yet (the server picks the kind). */
const ANY_KIND = 'any';

/**
 * The sample pitch (design outreach-v2 d1, d2): one P4 call for a sample person of the selected
 * kind, with `[first name]` and `[playlist]` as holes. No reveal, no send, not stored. A tab writes
 * its first sample when it opens; later samples only on "Write again". Each refusal of the server
 * shows its own box, and the last sample of the tab stays.
 */
const MessagePreviewComponent: React.FC<MessagePreviewProps> = ({ accountId, campaignUuid, kinds }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { isDemo } = useOutreachPlan();
  const { previewMessage, isPreviewing } = useCampaignActions(accountId);
  const [picked, setPicked] = useState<OutreachPitchKind | null>(null);
  const selected = picked !== null && kinds.includes(picked) ? picked : (kinds[0] ?? null);
  const key = selected ?? ANY_KIND;
  const [samples, setSamples] = useState<Record<string, SamplePitch>>({});
  const [problems, setProblems] = useState<Record<string, SamplePitchProblem>>({});
  const opened = useRef(new Set<string>());

  const run = useCallback(() => {
    setProblems((current) => {
      const { [key]: _gone, ...rest } = current;
      return rest;
    });
    previewMessage({ campaignUuid, kind: selected })
      .then((sample) => setSamples((current) => ({ ...current, [key]: sample })))
      .catch((error: unknown) => setProblems((current) => ({ ...current, [key]: samplePitchProblem(error) })));
  }, [campaignUuid, key, previewMessage, selected]);

  useEffect(() => {
    if (isDemo || opened.current.has(key)) {
      return;
    }
    opened.current.add(key);
    run();
  }, [isDemo, key, run]);

  // The story line of the song lives in Your sound (controller ruling, Task 3 concern 2).
  const openSound = useCallback(() => router.push(SMLRoutes.outreachProfile), [router]);
  const sample = samples[key];
  const problem = problems[key];

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.sampleTitle)}
        right={
          isPreviewing ? (
            <span className={shared.small} data-testid="sample-writing">
              {intl.formatMessage(messages.sampleWriting)}
            </span>
          ) : (
            <SMLButton size="sm" outline omitCap leftIcon={faArrowsRotate} onClick={run} disabled={isDemo}>
              {intl.formatMessage(sample === undefined ? messages.sampleRun : messages.sampleAgain)}
            </SMLButton>
          )
        }
      />
      <PanelBody>
        <div className={shared.stack} data-testid="builder-preview">
          <p className={shared.small}>{intl.formatMessage(messages.sampleNote)}</p>
          <SampleKindTabs kinds={kinds} selected={selected} onSelect={setPicked} />
          {problem !== undefined && <SamplePitchNotice problem={problem} onRetry={run} onOpenSound={openSound} />}
          {sample !== undefined && <SamplePitchText sample={sample} />}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const MessagePreview = TypedMemo(MessagePreviewComponent);
